import type { NextPage, GetStaticPaths } from 'next';
import Head from 'next/head';
import Script from 'next/script';

import dynamic from 'next/dynamic';
import { fetchData } from '../../../utils/fetch';
import {
  QuizEntityDataType, ResponseSuccessQuizEntityDataType, ResponseErrorQuizEntityDataType, QuizType, QuizProgressStates,
} from '../../../types/quiz';
import { QuizFlags, StatusCodes } from '../../../utils/enums';
import { getAbsImagePath } from '../../../utils/utilFunctions';

type Props = {
  quizId?: string;
  quizNotFound?: boolean;
  quizEnded?: boolean;
  quizInactive?: boolean;
  asyncQuiz: boolean,
  quizData: QuizEntityDataType
};

const QuizNotFound = dynamic(() => import('../../../components/QuizContainer/QuizNotFound/QuizNotFound'));
const QuizContainer = dynamic(() => import('../../../components/QuizContainer/QuizContainer'));

const QuizPage: NextPage<Props> = ({
  quizEnded,
  quizInactive,
  quizNotFound,
  quizId,
  quizData,
  asyncQuiz,
}) => (
  <div className="h-1/1 w-1/1">
    <Head>
      <title>VQuiz</title>
      <meta name="description" content="Vedantu Vquiz" />
      <link rel="icon" href={getAbsImagePath('/cdn/images/favicon.png')} />
    </Head>
    <Script src={getAbsImagePath('/cdn/js/login-signup/login-signup.js')} strategy="beforeInteractive" />
    <Script src={getAbsImagePath('/cdn/js/dotlottie-player.js')} strategy="lazyOnload" />
    {/* <Script src={getAbsImagePath('/cdn/js/dotlottie-player.js')} /> */}
    <div className="h-1/1 w-1/1">
      {quizNotFound && <QuizNotFound /> }
      {
        quizData
        && (
        <QuizContainer
          asyncQuiz={asyncQuiz}
          quizId={quizId}
          quizData={quizData}
          quizEnded={quizEnded || false}
          quizInactive={quizInactive || false}
        />
        )
      }
    </div>
  </div>
);

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [
    // { params: { quizId: '1' } }, // example
  ],
  fallback: true, //
});

export const getStaticProps = async ({ params }: { params: { quizId: string } }) => {
  // api call to get quiz data
  let resp;
  let quizNotFound = null;
  let quizEnded = null;
  let asyncQuiz = null;
  let quizInactive = null;
  try {
    resp = (await fetchData<ResponseSuccessQuizEntityDataType>({ url: `/assessment/v1/quiz/${params.quizId}`, credentials: 'include' })).response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('error', error);
    const err = error as ResponseErrorQuizEntityDataType;

    if (err.error?.code === StatusCodes.NOT_FOUND || err.error?.code === StatusCodes.BAD_REQUEST) {
      quizNotFound = true;
    }
  }

  switch (resp?.type) {
    case QuizType.LIVE: {
      if (!resp.allowLateJoinees && resp.progressState === QuizProgressStates.LIVE_STARTED) {
        quizEnded = true;
      }
      if (Date.now() < resp.startTime) {
        quizInactive = true;
      }
      if (resp.progressState === QuizProgressStates.LIVE_ENDED) {
        quizEnded = true;
      }
      break;
    }
    case QuizType.ON_DEMAND: {
      if (Date.now() > resp.onDemandEndTime) {
        quizEnded = true;
      }
      if (Date.now() > resp.startTime) {
        if (resp.flags?.includes(QuizFlags.PRE_QUIZ_TASKS_COMPLETED)) {
          asyncQuiz = true;
        } else {
          quizInactive = true;
        }
      }
      break;
    }
    case QuizType.HYBRID: {
      if (resp.progressState === QuizProgressStates.ON_DEMAND_ENDED) {
        quizEnded = true;
      }
      if (Date.now() > resp.liveEndTime || resp.progressState === QuizProgressStates.LIVE_ENDED) {
        if (resp.flags?.includes(QuizFlags.LIVE_POST_QUIZ_TASKS_COMPLETED)) {
          asyncQuiz = true;
        } else {
          quizInactive = true;
        }
      }
      if (Date.now() < resp.startTime) {
        quizInactive = true;
      }
      break;
    }
    default: break;
  }

  return {
    props: {
      quizId: params.quizId,
      quizNotFound,
      quizEnded,
      quizInactive,
      asyncQuiz,
      quizData: resp ?? null,
    },
    revalidate: 10, //  called by serverless function every 10 seconds only when clients visit the page
  };
};

export default QuizPage;
